import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import './NotFound.css';

interface Course {
  id: string;
  title: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  urlSlug?: string;
}

interface Keynote {
  id: string;
  title: string;
  city?: string;
  date?: string;
  url?: string;
}

const NotFound: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [keynotes, setKeynotes] = useState<Keynote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourses();
    fetchKeynotes();
  }, []);

  const fetchCourses = async () => {
    try {
      const client = generateClient<Schema>({ authMode: 'iam' });
      const { data } = await client.models.Course.list({
        filter: {
          isActive: { eq: true }
        }
      });
      setCourses((data || []) as Course[]);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchKeynotes = async () => {
    try {
      const client = generateClient<Schema>({ authMode: 'iam' });
      const { data } = await client.models.Keynote.list({
        filter: {
          isActive: { eq: true }
        }
      });
      setKeynotes((data || []) as Keynote[]);
    } catch (err) {
      console.error('Error fetching keynotes:', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="page">
      {/* Full-width section with brand color background */}
      <section className="not-found-section">
        <div className="container">
          <div className="not-found-content">
            {/* 404 Number with Animation */}
            <div className="error-number">
              <span className="four">4</span>
              <span className="zero">0</span>
              <span className="four">4</span>
            </div>
            
            {/* Clean Error Message Block */}
            <div className="error-text-block">
              <h1>Oops! Seite nicht gefunden</h1>
              <p>
                Die Seite, die Sie suchen, existiert nicht oder wurde verschoben. 
                Keine Sorge – wir helfen Ihnen gerne weiter!
              </p>
            </div>



            {/* Available Courses */}
            {!loading && courses.length > 0 && (
              <div className="available-courses">
                <h3>Unsere KI-Kurse:</h3>
                <div className="courses-grid">
                  {courses.map((course) => (
                    <Link 
                      key={course.id} 
                      to={course.urlSlug ? `/courses/${course.urlSlug}` : '#'} 
                      className="not-found-course-card"
                    >
                      <h4 className="not-found-course-title">{course.title}</h4>
                      <div className="not-found-course-info">
                        {course.location && (
                          <div className="not-found-info-item">{course.location}</div>
                        )}
                        {course.startDate && course.endDate && (
                          <div className="not-found-info-item">
                            {new Date(course.startDate).toLocaleDateString('de-DE')} - {new Date(course.endDate).toLocaleDateString('de-DE')}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Available Keynotes */}
            {!loading && keynotes.length > 0 && (
              <div className="available-courses">
                <h3>Unsere KI-Impulse:</h3>
                <div className="courses-grid">
                  {keynotes.map((keynote) => (
                    <a 
                      key={keynote.id} 
                      href={keynote.url || '#'} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="not-found-course-card"
                    >
                      <h4 className="not-found-course-title">{keynote.title}</h4>
                      <div className="not-found-course-info">
                        {keynote.city && (
                          <div className="not-found-info-item">{keynote.city}</div>
                        )}
                        {keynote.date && (
                          <div className="not-found-info-item">
                            {new Date(keynote.date).toLocaleDateString('de-DE')}
                          </div>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
